/* eslint-disable max-lines */

import {autobind} from 'core-decorators';
import {clone, debounce} from 'lodash';
import {Sidebar, SidebarProps, SidebarBody, SidebarHead} from '../Sidebar';
import Button from '@material-ui/core/Button';
import Checkbox, {CheckboxProps} from '@material-ui/core/Checkbox';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DraggableList from '../../DraggableList';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import memoize from 'lodash-decorators/memoize';
import React from 'react';
import {SPRING_1, GRANITE_7} from '../../../../colors';
import theme from '../../../../theme';
import LayerListItem from '../../LayerListItem';
import OverlaysSidebarButton from './OverlaysSidebarButton';
import {UserLayerDispatcher} from '../../../../hooks/useLayers';
import AddIcon from '@material-ui/icons/Add';
import {withStyles} from '@material-ui/core/styles';
import {CSSProperties} from '@material-ui/styles';

const styles: Record<'addMapLayersButtonLabel', CSSProperties> = {
    addMapLayersButtonLabel: {
        justifyContent: 'start',
        fontSize: 14,
        color: theme.palette.primary.main,
    }
};
import trackLayerAnalytics from '../../../../utils/gaia/trackLayerAnalytics';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';

export interface LayersSidebarProps extends SidebarProps {
    showGridSelect: boolean;
    overlays: MapOverlays;
    onOverlaysChange: SetState<MapOverlays>;
    onSidebarChange: (sidebar: MapSidebar) => void;
    onGridChange(gridId: Grid): void;
    grid: Grid;
    interactionType: MapInteraction['type'];
    userLayers: UserLayer[] | undefined;
    userLayerDispatcher: UserLayerDispatcher;
    highlight: MapHighlight;
    layerTypes: {[key: string]: LayerType} | undefined;
    disableShowWaypoints: boolean;
    disableShowWaypointsReason: string | undefined;
    disableShowTracks: boolean;
    disableShowTracksReason: string | undefined;
    disableShowAreas: boolean;
    disableShowAreasReason: string | undefined;
    isLayered: boolean | undefined;
}

interface LayersSidebarState {
    isDragging: boolean;
    isHoveringOpacity: boolean;
}

type DraggableListValue = 'Visible' | 'Hidden' | UserLayer;

const debouncedChangeLayerOpacityEvent = debounce((layerName, opacity, userLayers) => {
    trackLayerAnalytics('Change Layer Opacity', userLayers, {
        layerName,
        opacity,
        category: 'map2',
    });
}, 1000, {leading: false, trailing: true});

class LayersSidebar extends React.Component<
    LayersSidebarProps & {classes: {addMapLayersButtonLabel: string}},
    LayersSidebarState
> {
    constructor(props: LayersSidebarProps & {classes: {addMapLayersButtonLabel: string}}) {
        super(props);
        this.state = {
            isDragging: false,
            isHoveringOpacity: false,
        };
    }

    @autobind
    onDraggableListChange(values: DraggableListValue[]) {
        const userLayers: UserLayer[] = [];
        let isVisible = true;

        values.forEach(value => {
            if (value === 'Visible' || value === undefined) return;
            else if (value === 'Hidden') isVisible = false;
            else userLayers.unshift({...value, isVisible});
        });
        this.props.userLayerDispatcher({type: 'update', layers: userLayers});
    }

    render() {
        return <Sidebar {...this.props}>
            <SidebarHead onSidebarChange={this.props.onSidebarChange} title='Map Layers' />
            <SidebarBody>
                {this.renderBody()}
                {/* This outer div draws a solid background without overriding hover styles. */}
                <div style={{
                    width: '100%',
                    background: 'white',
                    position: 'sticky',
                    display: 'flex',
                    bottom: 0,
                    borderTopColor: GRANITE_7,
                    borderTopWidth: 1,
                    borderTopStyle: 'solid',
                }}>
                    <Button
                        color="primary"
                        onClick={this.onMapLayersButtonClick}
                        style={{
                            borderRadius: 0,
                            paddingLeft: theme.spacing(2),
                            paddingTop: theme.spacing(2),
                            paddingBottom: theme.spacing(2),
                            flex: 1,
                        }}
                        disableRipple
                        classes={{label: this.props.classes.addMapLayersButtonLabel}}
                    >
                        <AddIcon style={{
                            width: 40,
                            height: 40,
                            boxSizing: 'border-box',
                            backgroundColor: SPRING_1,
                            padding: theme.spacing(1),
                            marginRight: theme.spacing(2),
                            borderRadius: theme.shape.borderRadius,
                            color: '#fff',
                        }} />
                    Add Map Layers
                    </Button>
                </div>
            </SidebarBody>
        </Sidebar>;
    }

    @autobind
    onMapLayersButtonClick() {
        trackLayerAnalytics('Show Add Layers', this.props.userLayers, {
            category: 'map2',
        });
        this.props.onSidebarChange({type: 'editLayers'});
    }

    renderBody() {
        if (!this.props.layerTypes || this.props.isLayered === undefined) return null;
        else if (this.props.isLayered) return this.renderBodyLayered();
        else return this.renderBodyNonlayered();
    }

    renderBodyLayered() {
        if (!this.props.userLayers || !this.props.layerTypes) return null;
        const userLayers = this.props.userLayers.slice().reverse();

        const visibleUserLayers = userLayers.filter(({isVisible}: UserLayer) => isVisible);
        const hiddenUserLayers = userLayers.filter(({isVisible}: UserLayer) => !isVisible);

        return (<DraggableList<DraggableListValue>
            onDragStart={() => this.setState({isDragging: true})}
            onDragEnd={() => this.setState({isDragging: false})}
            isTopDroppable={false}
            onDraggableListChange={this.onDraggableListChange}
            dragStyle={{
                boxShadow: theme.shadows[5],
                borderRadius: theme.shape.borderRadius,
                opacity: 0.5
            }}
            placeholderStyle={{
                opacity: 0.25,
            }}
        >
            <ListSubheader data-draggable="Visible" disableSticky>Visible Layers</ListSubheader>
            <OverlaysSidebarButton onSidebarChange={this.props.onSidebarChange} />

            {visibleUserLayers.map((userLayer: UserLayer) => {
                if (!this.props.layerTypes) return null;
                const isOnlyVisibleLayer = visibleUserLayers.length === 1;

                return <LayerListItem
                    key={userLayer.layer.type}
                    layerType={this.props.layerTypes[userLayer.layer.type]}
                    onSidebarChange={this.props.onSidebarChange}
                    sidebar={{type: 'layers'}}
                    draggable={!isOnlyVisibleLayer}
                    style={{cursor: isOnlyVisibleLayer ? 'pointer' : '-webkit-grab'}}
                    data-draggable={userLayer}
                    secondary={this.renderOpacitySlider(userLayer)}
                >
                    <VisibilityCheckbox
                        checked={true}
                        onChange={this.toggleSelectedLayerVisible(userLayer)}
                        disabled={isOnlyVisibleLayer}
                    />
                </LayerListItem>;
            })}

            <ListSubheader data-draggable="Hidden" disableSticky>Hidden Layers</ListSubheader>

            {hiddenUserLayers.map((userLayer: UserLayer) => {
                if (!this.props.layerTypes) return null;
                return <LayerListItem
                    layerType={this.props.layerTypes[userLayer.layer.type]}
                    onSidebarChange={this.props.onSidebarChange}
                    sidebar={{type: 'layers'}}
                    key={userLayer.layer.type}
                    draggable
                    data-draggable={userLayer}
                    onClick={this.toggleSelectedLayerVisible(userLayer)}
                >
                    <VisibilityCheckbox
                        checked={false}
                        onChange={this.toggleSelectedLayerVisible(userLayer)}
                    />
                </LayerListItem>;
            })}
        </DraggableList>);
    }

    renderBodyNonlayered() {
        if (!this.props.userLayers) return null;
        return (<List style={{marginTop: theme.spacing(1)}} disablePadding>
            <OverlaysSidebarButton onSidebarChange={this.props.onSidebarChange} />
            {this.props.userLayers.slice().reverse().map((userLayer: UserLayer) => {
                if (!this.props.layerTypes) return null;
                return <LayerListItem
                    layerType={this.props.layerTypes[userLayer.layer.type]}
                    onSidebarChange={this.props.onSidebarChange}
                    sidebar={{type: 'layers'}}
                    key={userLayer.layer.type}
                    data-draggable={userLayer}
                    onClick={this.toggleSelectedLayerVisible(userLayer)}
                >
                    <VisibilityCheckbox
                        checked={userLayer.isVisible}
                        onChange={this.toggleSelectedLayerVisible(userLayer)}
                    />
                </LayerListItem>;
            })}
        </List>);
    }

    @memoize
    @autobind
    toggleSelectedLayerVisible(userLayer: UserLayer) {
        return () => {
            if (!this.props.userLayers) return;

            const nextSelectedLayer = {...userLayer, isVisible: !userLayer.isVisible};
            const index = this.props.userLayers.indexOf(userLayer);

            let nextUserLayers;
            if (this.props.isLayered) {
                nextUserLayers = clone(this.props.userLayers);
                nextUserLayers.splice(index, 1);
                nextUserLayers.push(nextSelectedLayer);
            } else {
                nextUserLayers = this.props.userLayers.map(
                    (l: UserLayer) => ({...l, isVisible: false})
                );
                nextUserLayers[index] = nextSelectedLayer;
            }

            this.props.userLayerDispatcher({type: 'update', layers: nextUserLayers});
        };
    }

    @memoize
    @autobind
    onOpacitySliderChange(userLayer: UserLayer) {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            debouncedChangeLayerOpacityEvent(
                userLayer.layer.type,
                Number(event.target.value),
                this.props.userLayers
            );
            if (!this.props.userLayers) return;

            const nextSelectedLayer = {
                ...userLayer,
                layer: {...userLayer.layer, opacity: Number(event.target.value)}
            };

            const index = this.props.userLayers.indexOf(userLayer);
            const nextUserLayers = clone(this.props.userLayers);
            nextUserLayers.splice(index, 1, nextSelectedLayer);

            this.props.userLayerDispatcher({type: 'update', layers: nextUserLayers});
        };
    }

    renderOpacitySlider(userLayer: UserLayer) {
        return <input
            key='slider'
            title='Opacity'
            type='range'
            value={userLayer.layer.opacity}
            min={0}
            max={1}
            step={0.01}
            style={{width: '100%', marginTop: theme.spacing(0.5)}}
            onChange={this.onOpacitySliderChange(userLayer)}
            onMouseEnter={() => this.setState({isHoveringOpacity: true})}
            onMouseLeave={() => this.setState({isHoveringOpacity: false})}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            // Prevent dragging the slider from dragging the whole layer
            draggable
            onDragStart={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
        />;
    }
}

function VisibilityCheckbox(props: CheckboxProps) {
    return <Checkbox
        disableRipple
        color='primary'
        checkedIcon={<CheckCircleIcon />}
        icon={<RadioButtonUncheckedOutlinedIcon />}
        style={{marginRight: -8}}
        {...props}
    />;
}

export default React.memo(withStyles(styles)(LayersSidebar));
